import React, { useState, useEffect } from "react";
import Date from "../../super_dashboard/components/Date";
import SelectLabels from "../../components/SelectLabels";
import { adminAxios, axiosInstance } from "../../axiosInstance";
import { updateData } from "../../utility/adminApi";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadImage from "../../components/UploadImage";
import DeliveryTimeInput from "../../components/DeliveryTimeInput";

function EditProduct({
  singleData,
  setSingleData,
  fetchProducts,
  setEdittab,
  currentPage,
}) {
  const [imageUrl, setImageUrl] = useState(singleData?.product_image || "");
  const [loading, setLoading] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [subCategoryData, setSubcategoryData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    singleData?.category?.id || null
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    singleData?.subcategory?.id || null
  );
  const [errorMessage, setErrorMessage] = useState("");

  // Log initial singleData for debugging
  useEffect(() => {
    console.log("Initial singleData:", singleData);
    fetchCategoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch categories
  const fetchCategoryData = async () => {
    try {
      const response = await adminAxios.get("categories");
      setCategoryData(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setErrorMessage("Failed to load categories. Please try again.");
    }
  };

  // Fetch subcategories based on category ID
  const fetchSubCatData = async (categoryId) => {
    try {
      const response = await adminAxios.get(
        `sub_categories/getbycat/${categoryId}`
      );
      setSubcategoryData(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setErrorMessage("Failed to load subcategories. Please try again.");
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      console.log("Sending image to /api/document/upload");
      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      console.log("Request headers:", axiosInstance.defaults.headers);

      const response = await axiosInstance.post(
        "/api/document/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log("Upload response:", response.data);

      const uploadedUrl = response?.data?.data?.url;
      if (uploadedUrl) {
        console.log("Upload successful:", uploadedUrl);
        setImageUrl(uploadedUrl);
        setSingleData((prevData) => ({
          ...prevData,
          product_image: uploadedUrl,
        }));
        setErrorMessage("");
        return uploadedUrl;
      } else {
        console.warn("Upload completed but no URL returned.");
        setErrorMessage("Upload completed but no URL returned.");
        return null;
      }
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error response:", error.response?.data);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to upload image. Please try again."
      );
      return null;
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      fetchSubCatData(selectedCategory);
    }
  }, [selectedCategory]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setSingleData((prevData) => ({
      ...prevData,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const handleDateChange = (year, month, day, type) => {
    setSingleData((prevData) => ({
      ...prevData,
      [type]: `${year}-${month}-${day}`,
    }));
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedSubcategory(null);
    setSingleData((prevData) => ({
      ...prevData,
      cat_id: value,
      sub_cat_id: null,
    }));
  };

  const handleSubcategoryChange = (value) => {
    setSelectedSubcategory(value);
    setSingleData((prevData) => ({
      ...prevData,
      sub_cat_id: value,
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Validate required fields
    if (!singleData.product_name) {
      setErrorMessage("The product name field is required.");
      return;
    }
    if (!singleData.is_unlimited && !(Number(singleData.available_quantity) > 0)) {
      setErrorMessage("Quantity must be greater than 0 for limited-stock products.");
      return;
    }
    if (!singleData.price) {
      setErrorMessage("The price field is required.");
      return;
    }

    setLoading(true);

    // JSON submission
    const data = {
      store: singleData.store,
      warehouse: singleData.warehouse,
      supplier: singleData.supplier,
      cat_id: Number(singleData.cat_id || selectedCategory),
      sub_cat_id: Number(singleData.sub_cat_id || selectedSubcategory),
      product_name: singleData.product_name,
      sku: singleData.sku,
      barcode: singleData.barcode,
      ...(singleData.is_unlimited
        ? {}
        : { quantity: Number(singleData.available_quantity) }),
      is_unlimited: !!singleData.is_unlimited,
      delivery_time: singleData.delivery_time || 0,
      price: Number(singleData.price),
      manufacturing_date: singleData.manufacturing_date,
      expiring_date: singleData.expiring_date,
      description: singleData.description,
      product_image: imageUrl || "",
    };

    console.log("Submitting JSON:", JSON.stringify(data, null, 2));

    try {
      console.log("Submitting to products/update/", singleData.id);
      console.log("Request headers:", {
        ...axiosInstance.defaults.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      });

      const response = await updateData(
        "products/update/",
        singleData.id,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );

      if (response instanceof Error || response?.response) {
        console.error("Update error:", response?.response?.data || response);
        setErrorMessage(
          response?.response?.data?.message || "Failed to update product."
        );
        setLoading(false);
      } else {
        console.log("Update successful");
        fetchProducts(currentPage);
        setEdittab(false);
        setLoading(false);
      }
    } catch (error) {
      console.error("Update error:", error);
      console.error("Error response:", error.response?.data);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to update product. Please try again."
      );
      setLoading(false);
    }
  };

  // const handleEditSubmit = async (e) => {
  //   e.preventDefault();
  //   setErrorMessage("");

  //   // Validate required fields
  //   if (!singleData.product_name) {
  //     setErrorMessage("The product name field is required.");
  //     return;
  //   }
  //   if (!singleData.available_quantity) {
  //     setErrorMessage("The quantity field is required.");
  //     return;
  //   }
  //   if (!singleData.price) {
  //     setErrorMessage("The price field is required.");
  //     return;
  //   }

  //   setLoading(true);

  //   // FormData submission (toggle to JSON if needed)
  //   const data = new FormData();
  //   const fields = {
  //     store: singleData.store,
  //     warehouse: singleData.warehouse,
  //     supplier: singleData.supplier,
  //     cat_id: Number(singleData.cat_id || selectedCategory),
  //     sub_cat_id: Number(singleData.sub_cat_id || selectedSubcategory),
  //     product_name: singleData.product_name,
  //     sku: singleData.sku,
  //     barcode: singleData.barcode,
  //     quantity: Number(singleData.available_quantity),
  //     price: Number(singleData.price),
  //     manufacturing_date: singleData.manufacturing_date,
  //     expiring_date: singleData.expiring_date,
  //     description: singleData.description,
  //     product_image: imageUrl || "",
  //   };

  //   console.log("FormData contents for update:");
  //   for (const [key, value] of Object.entries(fields)) {
  //     if (value !== undefined && value !== null) {
  //       console.log(`${key}:`, value);
  //       data.append(key, value);
  //     }
  //   }

  //   try {
  //     console.log("Submitting to products/update/", singleData.id);
  //     console.log("Request headers:", axiosInstance.defaults.headers);
  //     const response = await updateData(
  //       "products/update/",
  //       singleData.id,
  //       data,
  //       {
  //         headers: { "Content-Type": "multipart/form-data" },
  //       }
  //     );
  //     const errorCheck = response?.response?.data?.error;

  //     if (errorCheck) {
  //       console.error("Update error:", response.response.data);
  //       setErrorMessage(
  //         response.response.data.message || "Failed to update product."
  //       );
  //       setLoading(false);
  //     } else {
  //       console.log("Update successful");
  //       fetchProducts(currentPage);
  //       setEdittab(false);
  //       setLoading(false);
  //     }
  //   } catch (error) {
  //     console.error("Update error:", error);
  //     console.error("Error response:", error.response?.data);
  //     setErrorMessage(
  //       error.response?.data?.message ||
  //         "Failed to update product. Please try again."
  //     );
  //     setLoading(false);
  //   }
  // };

  return (
    <form onSubmit={handleEditSubmit} className="bg-white p-6 rounded-md">
      <div className="flex justify-end">
        <CancelIcon
          className="cursor-pointer"
          onClick={() => setEdittab(false)}
        />
      </div>
      <h4 className="text-center font-semibold text-xl">
        Edit Product Details
      </h4>
      {errorMessage && (
        <p className="text-red-600 text-sm mb-4">{errorMessage}</p>
      )}
      <div className="flex">
        <UploadImage
          imageUrl={imageUrl}
          setImageUrl={setImageUrl}
          onImageUpload={handleImageUpload}
        />
      </div>
      <div className="flex flex-col w-full mt-4">
        {/* <label htmlFor="product_image_url">Product Image URL</label> */}
        <input
          type="hidden"
          name="product_image"
          className="border p-2"
          value={typeof imageUrl === "string" ? imageUrl : ""}
          readOnly
        />
      </div>
      <div className="flex flex-col lg:flex-row gap-x-4 gap-y-4 flex-wrap">
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="store">Store</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Store"
            type="text"
            name="store"
            value={singleData.store || ""}
            onChange={handleChange}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="warehouse">Warehouse</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Warehouse"
            type="text"
            name="warehouse"
            value={singleData.warehouse || ""}
            onChange={handleChange}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="supplier">Supplier</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Supplier"
            type="text"
            name="supplier"
            value={singleData.supplier || ""}
            onChange={handleChange}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="category">Category</label>
          <SelectLabels
            categoryData={categoryData}
            text="Select a Category"
            valueKey="id"
            labelKey="cat_name"
            onSelectChange={handleCategoryChange}
            previousValue={singleData.cat_id || selectedCategory}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="subcategory">Subcategory</label>
          <SelectLabels
            categoryData={subCategoryData}
            text="Select Sub-category"
            valueKey="id"
            labelKey="sub_cat_name"
            onSelectChange={handleSubcategoryChange}
            previousValue={singleData.sub_cat_id || selectedSubcategory}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="product_name">Product Name</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Product Name"
            type="text"
            name="product_name"
            value={singleData.product_name || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="sku">SKU</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="SKU"
            type="text"
            name="sku"
            value={singleData.sku || ""}
            onChange={handleChange}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="barcode">Barcode</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Barcode"
            type="text"
            name="barcode"
            value={singleData.barcode || ""}
            onChange={handleChange}
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="available_quantity">Quantity</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Quantity"
            type="number"
            min="1"
            name="available_quantity"
            value={singleData.is_unlimited ? "" : singleData.available_quantity || ""}
            onChange={handleChange}
            disabled={!!singleData.is_unlimited}
            required={!singleData.is_unlimited}
          />
          <label className="flex items-center gap-2 mt-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!singleData.is_unlimited}
              onChange={(e) =>
                setSingleData((prev) => ({
                  ...prev,
                  is_unlimited: e.target.checked,
                }))
              }
            />
            Unlimited stock
          </label>
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label>Delivery Time</label>
          <DeliveryTimeInput
            seconds={singleData.delivery_time}
            onChange={(seconds) =>
              setSingleData((prev) => ({ ...prev, delivery_time: seconds }))
            }
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="price">Price</label>
          <input
            className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
            placeholder="Price"
            type="number"
            name="price"
            value={singleData.price || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="manufacturing_date">Manufacturing Date</label>
          <Date
            onDateChange={handleDateChange}
            type="manufacturing_date"
            existingDate={singleData.manufacturing_date}
            className="lg:w-full"
          />
        </div>
        <div className="flex flex-col w-full lg:w-1/3 lg:max-w-[32%]">
          <label htmlFor="expiring_date">Expiring Date</label>
          <Date
            onDateChange={handleDateChange}
            type="expiring_date"
            existingDate={singleData.expiring_date}
            className="lg:w-full"
          />
        </div>
        <div className="w-[99%] flex flex-col">
          <label htmlFor="description">Product Description</label>
          <textarea
            name="description"
            id="product_description"
            className="border-2 p-2"
            placeholder="Product description"
            onChange={handleChange}
            value={singleData.description || ""}
          ></textarea>
        </div>
      </div>
      <div className="flex justify-center my-6 text-white font-medium text-xl p-3 bg-darkblue rounded-lg">
        <button type="submit" className="w-full h-full" disabled={loading}>
          {loading ? "Updating..." : "Update Product"}
        </button>
      </div>
    </form>
  );
}

export default EditProduct;
